import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Calendar, CheckCircle, XCircle, Clock, Filter, Hash, TrendingUp, AlertCircle, CalendarDays } from "lucide-react";
import { getApiUrl } from "../../config/api";
import "../../styles/DashboardLayout.css";
import "../../styles/UtopiaPages.css";
import { useActiveLearner } from "../../hooks/useActiveLearner";
import { getStudentAuthHeaders } from "../../utils/studentSession";

interface AttendanceRecord {
  id: string;
  date: string;
  status: string;
  className?: string;
}

const MyAttendance: React.FC = () => {
  const token = localStorage.getItem("token") || "";
  const { activeLearnerId } = useActiveLearner();
  const [loading, setLoading] = useState(true);
  const [records, setRecords] = useState<AttendanceRecord[]>([]);
  const [stats, setStats] = useState({ present: 0, absent: 0, total: 0, percentage: 0 });
  const [filterMonth, setFilterMonth] = useState<string>("all");
  const [error, setError] = useState<string>("");

  const fetchAttendance = useCallback(async () => {
    if (!token) { setError("Authorization expired."); setLoading(false); return; }
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(getApiUrl("attendance/student"), {
        headers: getStudentAuthHeaders(token)
      });
      const data = res.data.records || [];
      const sorted = data.sort((a: any, b: any) => new Date(b.date).getTime() - new Date(a.date).getTime());
      setRecords(sorted);

      const present = data.filter((r: any) => r.status === 'present').length;
      const total = data.length;
      setStats({
        present,
        absent: total - present,
        total,
        percentage: total > 0 ? Math.round((present / total) * 100) : 0
      });
    } catch (err: any) {
      setError(err.response?.status === 403 ? "Access restricted to authorized students only." : "Synchronicity error: Participation data unreachable.");
    } finally { setLoading(false); }
  }, [token, activeLearnerId]);

  useEffect(() => { fetchAttendance(); }, [fetchAttendance]);

  const filteredRecords = filterMonth === "all" ? records : records.filter(r => {
    const d = new Date(r.date);
    const m = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    return m === filterMonth;
  });

  const availableMonths = Array.from(new Set(records.map(r => {
    const d = new Date(r.date);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  }))).sort().reverse();

  return (
    <div className="dashboard-container">
      <main className="dashboard-main">
        <div className="page-title-section">
          <div>
            <h1>Participation Metrics</h1>
            <p className="page-subtitle">Personalized overview of academic presence and engagement consistency</p>
          </div>
        </div>

        {/* Analytics Section */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 p-8 pt-0">
          <div className="bg-white p-6 rounded-3xl border border-slate-100 shadow-sm">
            <div className="w-10 h-10 bg-teal-50 text-teal-600 rounded-xl flex items-center justify-center mb-4"><TrendingUp size={20} /></div>
            <p className="text-[10px] font-bold text-slate-600 uppercase tracking-widest">Consistency Rate</p>
            <h4 className="text-2xl font-black text-slate-800">{stats.percentage}%</h4>
          </div>
          <div className="bg-white p-6 rounded-3xl border border-slate-100 shadow-sm">
            <div className="w-10 h-10 bg-blue-50 text-blue-600 rounded-xl flex items-center justify-center mb-4"><CheckCircle size={20} /></div>
            <p className="text-[10px] font-bold text-slate-600 uppercase tracking-widest">Present Enquiries</p>
            <h4 className="text-2xl font-black text-slate-800">{stats.present} <span className="text-xs text-slate-500 font-semibold">Days</span></h4>
          </div>
          <div className="bg-white p-6 rounded-3xl border border-slate-100 shadow-sm">
            <div className="w-10 h-10 bg-red-50 text-red-500 rounded-xl flex items-center justify-center mb-4"><AlertCircle size={20} /></div>
            <p className="text-[10px] font-bold text-slate-600 uppercase tracking-widest">Total Absences</p>
            <h4 className="text-2xl font-black text-slate-800">{stats.absent} <span className="text-xs text-slate-500 font-semibold">Days</span></h4>
          </div>
          <div className="bg-white p-6 rounded-3xl border border-slate-100 shadow-sm">
            <div className="w-10 h-10 bg-purple-50 text-purple-600 rounded-xl flex items-center justify-center mb-4"><CalendarDays size={20} /></div>
            <p className="text-[10px] font-bold text-slate-600 uppercase tracking-widest">Academic Sessions</p>
            <h4 className="text-2xl font-black text-slate-800">{stats.total}</h4>
          </div>
        </div>

        <div className="utopia-page-container">
          <div className="utopia-table-header p-8 flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <h2>Chronological Logs</h2>
              <p className="text-xs text-slate-600 font-medium mt-1">Detailed record of registered academic inputs</p>
            </div>
            <div className="flex items-center gap-3">
              <Filter size={16} className="text-slate-500" />
              <select className="utopia-input h-14 text-sm font-bold min-w-[240px]" value={filterMonth} onChange={e => setFilterMonth(e.target.value)}>
                <option value="all">Full Academic Calendar</option>
                {availableMonths.map(m => (
                  <option key={m} value={m}>{new Date(m + "-01").toLocaleDateString(undefined, { month: 'long', year: 'numeric' })}</option>
                ))}
              </select>
            </div>
          </div>

          {loading ? (
            <div className="p-32 text-center text-slate-500"><Clock className="animate-spin mx-auto mb-2" /> Initializing...</div>
          ) : error ? (
            <div className="p-20 text-center mx-8 my-8 bg-red-50 rounded-3xl border border-red-100">
              <AlertCircle className="mx-auto text-red-400 mb-4" size={48} />
              <p className="text-red-700 font-bold">{error}</p>
            </div>
          ) : filteredRecords.length === 0 ? (
            <div className="p-32 text-center text-slate-400">
              <Calendar className="mx-auto mb-4 opacity-10" size={64} />
              <p className="font-bold text-slate-600">Zero participation entries detected for selected parameters.</p>
            </div>
          ) : (
            <table className="utopia-table">
              <thead>
                <tr>
                  <th className="text-slate-700">Registry Date</th>
                  <th className="text-slate-700">Class Assignment</th>
                  <th className="text-slate-700">Day</th>
                  <th className="text-right text-slate-700">Status</th>
                </tr>
              </thead>
              <tbody>
                {filteredRecords.map(r => {
                  const rd = new Date(r.date);
                  return (
                    <tr key={r.id}>
                      <td className="font-bold text-slate-800">{rd.toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' })}</td>
                      <td>
                        <span className="utopia-badge bg-blue-50 text-blue-700 border border-blue-200 text-[10px] font-bold">
                          {r.className || "General"}
                        </span>
                      </td>
                      <td className="text-slate-600 text-sm font-medium">{rd.toLocaleDateString(undefined, { weekday: 'long' })}</td>
                      <td className="text-right">
                        <span className={`utopia-badge ${r.status === 'present' ? 'bg-teal-50 text-teal-600 border-teal-100' : r.status === 'absent_with_permission' ? 'bg-amber-50 text-amber-600 border-amber-100' : 'bg-red-50 text-red-500 border-red-100'} uppercase font-black text-[9px] px-3`}>
                          {r.status === 'absent_with_permission' ? 'Excused' : r.status}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}

        </div>
      </main>
    </div>
  );
};

export default MyAttendance;










